/**
 * Manages the HMAC-SHA256 signed search page token.
 *
 * The token proves that the current user's browser rendered a search page via
 * Nuxt SSR rather than hitting /api/v2/search directly. It is generated once
 * server-side on each full search page load and hydrated into client state.
 *
 * Token format:  <base64url(payloadJSON)>.<base64url(HMAC-SHA256 signature)>
 * Payload JSON:  { "iat": <unix seconds>, "jti": "<nonce>" }
 *
 * Both the Nuxt server and the ASP.NET backend share SEARCH_TOKEN_SECRET /
 * NUXT_SEARCH_TOKEN_SECRET and independently verify the signature + TTL.
 */

export interface SearchTokenState {
    /** The signed token string: <base64url(payloadJSON)>.<base64url(HMAC-SHA256 signature)> */
    value: string;
    /** Unix timestamp (seconds) when the token was issued. */
    iat: number;
}

function base64UrlEncode(buffer: ArrayBuffer): string {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=/g, "");
}

// Module-level cache so the CryptoKey is imported only once per server process
// regardless of how many requests call useSearchToken().
let _cachedKey: CryptoKey | null = null;

export function useSearchToken() {
    // Single state object so the token string and its issuance time are always in sync.
    const tokenState = useState<SearchTokenState | null>("searchToken", () => null);

    // The secret is a static runtime config value, so one import is sufficient.
    async function getKey(secret: string): Promise<CryptoKey> {
        if (!_cachedKey) {
            _cachedKey = await crypto.subtle.importKey(
                "raw",
                new TextEncoder().encode(secret),
                { name: "HMAC", hash: "SHA-256" },
                false,
                ["sign"]
            );
        }
        return _cachedKey;
    }

    async function generateToken(): Promise<void> {
        const config = useRuntimeConfig();
        const secret = config.searchTokenSecret as string;
        if (!secret) return;

        const iat = Math.floor(Date.now() / 1000);
        const jti = crypto.randomUUID().replace(/-/g, "");

        const payloadJson = JSON.stringify({ iat, jti });
        const payloadB64 = base64UrlEncode(new TextEncoder().encode(payloadJson).buffer);

        const key = await getKey(secret);
        const sigBuffer = await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(payloadB64));

        // Atomic update: value and iat always correspond to the same token.
        tokenState.value = {
            value: `${payloadB64}.${base64UrlEncode(sigBuffer)}`,
            iat,
        };
    }

    function isExpired(ttlMinutes: number): boolean {
        // A null tokenState means the secret was not configured; treat as not expired
        // so callers do not attempt a refresh loop.
        const iat = tokenState.value?.iat ?? 0;
        if (!iat) return false;
        const CLOCK_LEEWAY_SECONDS = 120;
        return Date.now() / 1000 > iat + ttlMinutes * 60 - CLOCK_LEEWAY_SECONDS;
    }

    return { tokenState, generateToken, isExpired };
}
